import { makeStyles, tokens } from '@fluentui/react-components';

export const useFREStyles = makeStyles({
  // Overlay that darkens the background
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },

  // Modal container
  modal: {
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    maxWidth: '520px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
        transform: 'scale(0.95)',
      },
      to: {
        opacity: 1,
        transform: 'scale(1)',
      },
    },
  },

  // Header section - default is for full-width screenshots
  header: {
    padding: 0,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '240px',
  },

  // Header variant for welcome step with gradient background
  headerWelcome: {
    background: 'linear-gradient(86.66deg, #6369D0 23.53%, #789CF3 53.57%, #69A4CE 109.52%)',
    padding: '24px',
    height: '200px',
  },

  // Screenshot image - fills entire header
  headerImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center',
  },

  // Welcome step emoji - centered with max height
  headerImageWelcome: {
    width: 'auto',
    height: 'auto',
    maxHeight: '160px',
    objectFit: 'contain',
    objectPosition: 'center',
  },

  // Content section
  content: {
    padding: '24px 32px',
    flex: 1,
    overflowY: 'auto',
  },

  // Step title
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: '0 0 12px 0',
    lineHeight: tokens.lineHeightBase500,
  },

  // Step description
  description: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    margin: 0,
    lineHeight: '1.5',
  },

  // Footer section with navigation
  footer: {
    padding: '16px 32px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  // Navigation buttons container
  navButtons: {
    display: 'flex',
    gap: '12px',
  },

  // Back button (outline style)
  backButton: {
    minWidth: '80px',
  },

  closeButton: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },

  // Next/Done button (primary style)
  nextButton: {
    minWidth: '80px',
  },

  // Step indicators (dots)
  stepIndicators: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    flex: 1,
  },

  liveAnnouncement: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },

  // Individual step dot
  stepDot: {
    width: '10px',
    height: '10px',
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'background-color 0.2s ease',
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      border: '1px solid CanvasText',
      backgroundColor: 'Canvas',
    },
  },

  // Completed step dot
  stepDotCompleted: {
    border: `1px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground,
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
      backgroundColor: 'CanvasText',
    },
  },

  // Active step dot
  stepDotActive: {
    border: `1px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground,
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '2px',
    '@media (forced-colors: active)': {
      border: '1px solid Highlight',
      backgroundColor: 'Highlight',
      outlineColor: 'CanvasText',
    },
  },

  // Screenshot preview container (for step 2 and 3)
  screenshotContainer: {
    marginTop: '16px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },

  // Screenshot image
  screenshot: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
});
